import {
  calculateTaxOnIncome,
  calculateMarginalRate,
  STANDARD_DEDUCTIONS,
  FICA_CONSTANTS,
  STATE_TAX_DATA,
} from '../calculations'

interface BonusRsuTaxInput {
  bonusAmount: number
  filingStatus: string
  regularIncome: number
  stateCode: string
  customStateTaxRate?: number
  isRSU?: boolean
}

export function calculateBonusRsuTax(input: BonusRsuTaxInput) {
  const deduction = STANDARD_DEDUCTIONS[input.filingStatus] || STANDARD_DEDUCTIONS.single

  // State tax rate
  const stateEntry = STATE_TAX_DATA.find(s => s.code === input.stateCode)
  const stateRate = stateEntry?.code === 'CUSTOM'
    ? (input.customStateTaxRate || 0)
    : (stateEntry?.rate || 0)

  // === Flat withholding method (what employers typically withhold) ===
  const flatFederalRate = 0.22 // IRS supplemental wage flat rate
  const flatFederalWithholding = input.bonusAmount * flatFederalRate
  const flatStateWithholding = input.bonusAmount * (stateRate / 100)

  // FICA on bonus
  const totalW2Income = input.regularIncome + input.bonusAmount
  const ssWagesBeforeBonus = Math.min(input.regularIncome, FICA_CONSTANTS.ssWageBase)
  const ssWagesAfterBonus = Math.min(totalW2Income, FICA_CONSTANTS.ssWageBase)
  const bonusSSTax = (ssWagesAfterBonus - ssWagesBeforeBonus) * FICA_CONSTANTS.ssRate

  const bonusMedicareTax = input.bonusAmount * FICA_CONSTANTS.medicareRate
  const additionalMedicare = totalW2Income > FICA_CONSTANTS.additionalMedicareThreshold
    ? Math.max(0, Math.min(input.bonusAmount, totalW2Income - FICA_CONSTANTS.additionalMedicareThreshold)) * FICA_CONSTANTS.additionalMedicareRate
    : 0

  const flatTotalWithholding = flatFederalWithholding + flatStateWithholding + bonusSSTax + bonusMedicareTax + additionalMedicare
  const flatTakeHome = input.bonusAmount - flatTotalWithholding

  // === Actual marginal tax liability ===
  const regularTaxableIncome = Math.max(0, input.regularIncome - deduction)
  const combinedTaxableIncome = Math.max(0, input.regularIncome + input.bonusAmount - deduction)

  const taxOnRegular = calculateTaxOnIncome(regularTaxableIncome, input.filingStatus)
  const taxOnCombined = calculateTaxOnIncome(combinedTaxableIncome, input.filingStatus)
  const actualFederalTax = taxOnCombined - taxOnRegular

  const actualStateTax = input.bonusAmount * (stateRate / 100)
  const actualTotalTax = actualFederalTax + actualStateTax + bonusSSTax + bonusMedicareTax + additionalMedicare
  const actualTakeHome = input.bonusAmount - actualTotalTax

  // Difference (positive = refund expected, negative = owe more)
  const federalDifference = flatFederalWithholding - actualFederalTax
  const totalDifference = flatTotalWithholding - actualTotalTax

  // Marginal rate on bonus
  const marginalRate = calculateMarginalRate(combinedTaxableIncome, input.filingStatus)
  const effectiveBonusRate = input.bonusAmount > 0 ? (actualTotalTax / input.bonusAmount) * 100 : 0

  return {
    bonusAmount: input.bonusAmount,
    regularIncome: input.regularIncome,
    isRSU: input.isRSU || false,
    // Flat withholding
    flatFederalWithholding: Math.round(flatFederalWithholding),
    flatStateWithholding: Math.round(flatStateWithholding),
    flatFICA: Math.round(bonusSSTax + bonusMedicareTax + additionalMedicare),
    flatTotalWithholding: Math.round(flatTotalWithholding),
    flatTakeHome: Math.round(flatTakeHome),
    // Actual liability
    actualFederalTax: Math.round(actualFederalTax),
    actualStateTax: Math.round(actualStateTax),
    actualFICA: Math.round(bonusSSTax + bonusMedicareTax + additionalMedicare),
    actualTotalTax: Math.round(actualTotalTax),
    actualTakeHome: Math.round(actualTakeHome),
    // Difference
    federalDifference: Math.round(federalDifference),
    totalDifference: Math.round(totalDifference),
    willGetRefund: totalDifference > 0,
    isNegligibleDifference: Math.abs(totalDifference) < 50,
    // Rates
    flatFederalRate: 22,
    marginalRate,
    effectiveBonusRate: Math.round(effectiveBonusRate * 10) / 10,
    stateRate,
    // FICA details
    bonusSSTax: Math.round(bonusSSTax),
    bonusMedicareTax: Math.round(bonusMedicareTax),
    additionalMedicare: Math.round(additionalMedicare),
  }
}
